// Noyau — FEUILLES D'ANALYSE DE PRÉPA ET DE TERMINALE au format publié : équations différentielles (plugin
// « ode »), intégrales (« integral »), dérivées partielles (« partial »), sommes de Riemann (« riemann »).
//
// Les juges sont ceux de l'ancien moteur (`packages/reasoner/src/kernel-{ode,integral,partial,riemann}.ts`,
// lots 6.60 à 6.69), transcrits en JavaScript sans changement dans `./analysis-legacy/` (un seul fichier, produit
// par esbuild) : mêmes lectures de lignes, mêmes gestes, mêmes messages, mêmes HORA.
//
// Le juge de la feuille (judge.js) appelle `createLegacyAnalysisSheet` puis `judgeLegacyAnalysisLine` ; chaque
// ancien juge ne modifie son état que dans sa fonction d'enregistrement : on copie ces champs-là avant
// l'enregistrement et on range la copie dans la ligne gardée (`.ana`), la feuille reste immuable.
// ============================================================================

import {
  createKIntState,
  createKOdeState,
  createKPartState,
  createKRiemState,
  kintCommit,
  kintGoalReached,
  kintIsAnswer,
  kintJudge,
  kintLineAtoms,
  kodeCommit,
  kodeAnswerWritten,
  kodeGoalReached,
  kodeIsAnswer,
  kodeJudge,
  kodeLineAtoms,
  kpartCommit,
  kpartGoalReached,
  kpartIsAnswer,
  kpartJudge,
  kpartLineAtoms,
  kriemCommit,
  kriemGoalReached,
  kriemIsAnswer,
  kriemJudge,
  kriemLineAtoms,
} from './analysis-legacy/kernel-analysis-legacy.js';

// les champs que chaque fonction d'enregistrement modifie
const cloneInt = (ks) => ({
  ...ks,
  written: new Set(ks.written),
  names: { ...ks.names },
  classes: new Map(ks.classes),
  side: new Map(ks.side),
});

const SORTS = {
  ode: {
    create: createKOdeState,
    atoms: (ks, t) => kodeLineAtoms(t, ks.fn, ks.x),
    judge: kodeJudge,
    commit: kodeCommit,
    done: kodeGoalReached,
    // deux bonnes réponses possibles : seule la solution écrite est cochée
    isAnswer: (ks, t) => kodeIsAnswer(ks, t) && kodeAnswerWritten(ks, t),
    clone: (ks) => ({
      ...ks,
      written: new Set(ks.written),
      eqs: [...ks.eqs],
      families: [...ks.families],
      cEqs: [...ks.cEqs],
      ones: [...ks.ones],
      derivs: [...ks.derivs],
      evals: new Map(ks.evals),
      values: new Map(ks.values),
      deltas: new Set(ks.deltas),
    }),
  },
  integral: {
    create: createKIntState,
    atoms: (_ks, t) => kintLineAtoms(t),
    judge: kintJudge,
    commit: kintCommit,
    done: kintGoalReached,
    isAnswer: kintIsAnswer,
    clone: cloneInt,
  },
  partial: {
    create: createKPartState,
    atoms: (ks, t) => kpartLineAtoms(t, ks.spec),
    judge: kpartJudge,
    commit: kpartCommit,
    done: kpartGoalReached,
    isAnswer: kpartIsAnswer,
    clone: (ks) => ({ ...ks, written: new Set(ks.written), facts: new Map(ks.facts) }),
  },
  riemann: {
    create: createKRiemState,
    atoms: (_ks, t) => kriemLineAtoms(t),
    judge: kriemJudge,
    commit: kriemCommit,
    done: kriemGoalReached,
    isAnswer: kriemIsAnswer,
    clone: (st) => ({
      ...st,
      written: new Set(st.written),
      subs: st.subs.map((s) => ({ ...s, ks: cloneInt(s.ks) })),
      forms: new Map([...st.forms].map(([k, v]) => [k, { ...v, forms: [...v.forms] }])),
      lims: new Map(
        [...st.lims].map(([k, l]) => [k, { ...l, ints: [...l.ints], writings: [...l.writings] }]),
      ),
    }),
  },
};

export const LEGACY_ANALYSIS_PLUGINS = Object.keys(SORTS);

export function createLegacyAnalysisSheet(model) {
  const sort = model.kernel.plugin;
  return { sort, ks: SORTS[sort].create(model) };
}

/** Le texte de départ affiché : l'équation, l'intégrale, la fonction ou la somme de l'énoncé. */
export function legacyAnalysisStartText(model) {
  return String(model.kernel.start ?? '');
}

/**
 * Une proposition du QCM est-elle la réponse ? Comme l'ancien moteur : la feuille est finie et la ligne est la
 * réponse (pour une équation différentielle, la solution écrite par l'élève).
 */
export function legacyAnalysisHasAnswer(state, text) {
  const S = SORTS[state.sort];
  try {
    return S.done(state.ks) && S.isAnswer(state.ks, String(text));
  } catch {
    return false;
  }
}

export function judgeLegacyAnalysisLine(state, text) {
  const S = SORTS[state.sort];
  const { ks } = state;
  let v;
  try {
    v = S.judge(ks, [{ atoms: S.atoms(ks, String(text)) }], { verify: true });
  } catch (e) {
    return {
      title: null,
      status: 'parse_error',
      kind: null,
      message: e instanceof Error ? e.message : String(e),
    };
  }
  const base = { title: v.title ?? null, message: v.message ?? null };
  const commitTo = () => {
    const next = S.clone(ks);
    S.commit(next, v);
    return { sort: state.sort, ks: next };
  };
  if (v.kind === 'step' || v.kind === 'ready') {
    const next = commitTo();
    const done = S.done(next.ks);
    const rule = v.rule;
    const gestures = rule
      ? [{ ruleId: rule.id, label: rule.label, why: rule.why, hora: rule.hora ?? 0 }]
      : [];
    return {
      ...base,
      status: done ? 'goal' : 'step',
      kind: 'step',
      gestures,
      hora: v.hora ?? rule?.hora ?? 0,
      message:
        v.message ?? (done ? 'Tu tiens la réponse !' : rule ? `Juste : ${rule.label}.` : 'Juste.'),
      ana: next,
    };
  }
  if (v.kind === 'useless') {
    const next = commitTo();
    return { ...base, status: 'useless', kind: 'kept', gestures: [], hora: 0, ana: next };
  }
  if (v.kind === 'unjustified')
    return { ...base, status: 'unjustified', kind: 'kept', gestures: [], hora: 0 };
  if (v.kind === 'false' || v.kind === 'trap')
    return { ...base, status: v.kind === 'trap' ? 'trap' : 'wrong', kind: null };
  return {
    ...base,
    status: 'parse_error',
    kind: null,
    message: v.message ?? 'Termine ta ligne.',
  };
}
